import React, { useState, useEffect, useCallback } from "react";
import { toast } from "sonner";
import { useWallet } from "@/context/WalletContext";
import { Panel, Pill } from "@/components/croc/ui";
import api from "@/lib/api";
import { CheckCircle2, XCircle, Clock, Gift, TrendingUp, TrendingDown } from "lucide-react";

const TIERS = [
  { key: "24h", label: "24h · 1/day" },
  { key: "90d", label: "90d · 2/12h" },
  { key: "180d", label: "180d · 3/8h" },
  { key: "360d", label: "360d · 4/6h" },
  { key: "720d", label: "720d · 6/4h" },
];

export const PredictionSchedule = () => {
  const { address } = useWallet();
  const [tier, setTier] = useState("360d");
  const [data, setData] = useState(null);
  const [busy, setBusy] = useState(null);

  const load = useCallback(() => {
    const a = address || "0x0000000000000000000000000000000000000000";
    api.predictionSchedule(a, tier).then(setData).catch(() => {});
  }, [address, tier]);

  useEffect(() => { load(); const t = setInterval(load, 15000); return () => clearInterval(t); }, [load]);

  // nudge the user before an open, unanswered slot closes so they don't lose the day's bonus
  useEffect(() => {
    if (!data?.slots || !address) return;
    const soon = data.slots.find((s) => s.status === "open" && !s.answered_side &&
      (new Date(s.closes) - Date.now()) < 60 * 60 * 1000);
    if (soon) {
      const mins = Math.max(1, Math.round((new Date(soon.closes) - Date.now()) / 60000));
      toast.warning(`Prediction closing in ${mins}m — answer to keep today's bonus`, { id: `pred-nudge-${soon.slot}` });
    }
  }, [data, address]);

  const minsLeft = (iso) => Math.max(0, Math.round((new Date(iso) - Date.now()) / 60000));

  const answer = async (slot, side) => {
    if (!address) return toast.error("Connect your wallet to predict");
    try {
      setBusy(`${slot}-${side}`);
      await api.predictionAnswer({ address, tier, slot, side });
      toast.success(`Prediction locked: ${side.toUpperCase()}`);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not submit");
    } finally { setBusy(null); }
  };

  return (
    <Panel className="mb-6" data-testid="prediction-schedule">
      <div className="flex items-center justify-between mb-4 flex-wrap gap-3">
        <div>
          <div className="font-display font-bold text-white">Daily Predictions · Polymarket</div>
          <div className="text-xs text-muted-foreground">Answer every prediction of the day to earn a bonus equal to your daily reward — paid with the 00:00 GST payout; miss one and it's forfeited.</div>
        </div>
        {data && !data.questions_unavailable && (
          <BonusPill status={data.bonus_status} required={data.required_per_day} bonus={data.bonus_usd} />
        )}
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {TIERS.map((t) => (
          <button key={t.key} onClick={() => setTier(t.key)} data-testid={`pred-tier-${t.key}`}
            className={`rounded-lg px-3 py-1.5 text-xs font-medium transition-colors ${tier === t.key ? "bg-[#1CFD4C] text-[#050806]" : "bg-[#0B120E] text-muted-foreground border border-white/10"}`}>
            {t.label}
          </button>
        ))}
      </div>

      {data?.questions_unavailable && <div className="text-sm text-muted-foreground py-6 text-center">Loading live markets…</div>}

      <div className="space-y-2">
        {(data?.slots ?? []).map((s) => (
          <div key={s.slot} className="rounded-xl border border-white/10 bg-[#090E0B] p-3.5" data-testid={`pred-slot-${s.slot}`}>
            <div className="flex items-start justify-between gap-3">
              <div className="min-w-0">
                <div className="text-sm text-white leading-snug">{s.question}</div>
                <div className="text-[10px] text-muted-foreground mt-1">Yes {Math.round(s.yes_price * 100)}% · No {Math.round(s.no_price * 100)}%</div>
              </div>
              <StatusChip status={s.status} answered={s.answered_side} correct={s.correct} winning={s.winning_side} />
            </div>
            {s.answered_side && s.correct === null && (
              <div className="text-[10px] text-[#1CFD4C] mt-1.5">Awaiting Polymarket result…</div>
            )}
            {s.answered_side && s.correct === false && s.winning && (
              <div className="text-[10px] text-red-400 mt-1.5">Resolved {s.winning.toUpperCase()} — your pick missed</div>
            )}
            {s.status === "open" && (
              <div className="text-[10px] text-[#1CFD4C] mt-1.5" data-testid={`pred-countdown-${s.slot}`}>Closes in {minsLeft(s.closes)}m</div>
            )}
            {s.status === "open" && !s.answered_side && (
              <div className="flex gap-2 mt-3">
                <button onClick={() => answer(s.slot, "yes")} disabled={busy} data-testid={`pred-yes-${s.slot}`}
                  className="flex-1 rounded-lg bg-[#10B981]/15 border border-[#10B981]/40 emerald-text py-2 text-sm font-semibold hover:bg-[#10B981]/25 transition-colors flex items-center justify-center gap-1">
                  <TrendingUp className="w-3.5 h-3.5" /> Yes</button>
                <button onClick={() => answer(s.slot, "no")} disabled={busy} data-testid={`pred-no-${s.slot}`}
                  className="flex-1 rounded-lg bg-red-500/15 border border-red-500/40 text-red-400 py-2 text-sm font-semibold hover:bg-red-500/25 transition-colors flex items-center justify-center gap-1">
                  <TrendingDown className="w-3.5 h-3.5" /> No</button>
              </div>
            )}
          </div>
        ))}
      </div>
    </Panel>
  );
};

function BonusPill({ status, required, bonus }) {
  const map = {
    earned: { tone: "emerald", text: `Bonus earned${bonus ? ` +$${Number(bonus).toFixed(2)}` : ""}` },
    pending: { tone: "gold", text: "Awaiting results" },
    forfeited: { tone: "danger", text: "Bonus forfeited today" },
    in_progress: { tone: "gold", text: `Answer all ${required} → +$${Number(bonus || 0).toFixed(2)}` },
  };
  const c = map[status] || map.in_progress;
  return (
    <Pill tone={c.tone} data-testid="prediction-bonus-status">
      <Gift className="w-3 h-3" /> {c.text}
    </Pill>
  );
}

function StatusChip({ status, answered, correct, winning }) {
  if (answered) {
    if (correct === true) return <span className="shrink-0 inline-flex items-center gap-1 text-[11px] emerald-text" data-testid="pred-chip-won"><CheckCircle2 className="w-3.5 h-3.5" /> WON</span>;
    if (correct === false) return <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-red-400" data-testid="pred-chip-lost"><XCircle className="w-3.5 h-3.5" /> LOST</span>;
    return <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-[#1CFD4C]" data-testid="pred-chip-pending"><Clock className="w-3.5 h-3.5" /> {answered.toUpperCase()} · PENDING</span>;
  }
  if (status === "open") return <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-[#1CFD4C]"><Clock className="w-3.5 h-3.5" /> Open</span>;
  if (status === "closed") return <span className="shrink-0 inline-flex items-center gap-1 text-[11px] text-red-400"><XCircle className="w-3.5 h-3.5" /> Missed</span>;
  return <span className="shrink-0 text-[11px] text-muted-foreground">Upcoming</span>;
}
